import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from './ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from './ui/select';
import { toast } from 'sonner';
import {
  Upload,
  Calendar,
  TrendingUp,
  TrendingDown,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  BarChart3,
  RefreshCw,
  FileText
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InventoryImport = () => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedStore, setSelectedStore] = useState('ashmiaa');
  const [stores, setStores] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importLogs, setImportLogs] = useState([]);
  const [showLogsDialog, setShowLogsDialog] = useState(false);
  const [selectedLogDetail, setSelectedLogDetail] = useState(null);
  
  // Date Analytics
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });
  const [selectedAnalyticsStore, setSelectedAnalyticsStore] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  
  // Date Detail Dialog
  const [showDateDetailDialog, setShowDateDetailDialog] = useState(false);
  const [dateDetailData, setDateDetailData] = useState(null);
  const [loadingDateDetail, setLoadingDateDetail] = useState(false);
  
  // Card Detail Dialog
  const [showCardDetailDialog, setShowCardDetailDialog] = useState(false);
  const [cardDetailType, setCardDetailType] = useState(null);
  const [cardDetailData, setCardDetailData] = useState(null);

  useEffect(() => {
    fetchStores();
    fetchImportLogs();
    fetchDateAnalytics();
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data || []);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchImportLogs = async () => {
    try {
      const response = await axios.get(`${API}/inventory/import-logs?limit=20`);
      if (response.data.success) {
        setImportLogs(response.data.logs);
      }
    } catch (error) {
      console.error('Error fetching import logs:', error);
    }
  };

  const fetchDateAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const params = new URLSearchParams({
        start_date: dateRange.start,
        end_date: dateRange.end
      });
      
      if (selectedAnalyticsStore && selectedAnalyticsStore !== 'all') {
        params.append('store_name', selectedAnalyticsStore);
      }
      
      const response = await axios.get(`${API}/inventory/date-analytics?${params}`);
      if (response.data.success) {
        setAnalytics(response.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const fetchDateDetails = async (date) => {
    setLoadingDateDetail(true);
    try {
      const params = new URLSearchParams({ date });
      if (selectedAnalyticsStore && selectedAnalyticsStore !== 'all') {
        params.append('store_name', selectedAnalyticsStore);
      }
      
      const response = await axios.get(`${API}/inventory/orders-by-date?${params}`);
      if (response.data.success) {
        setDateDetailData(response.data);
        setShowDateDetailDialog(true);
      }
    } catch (error) {
      console.error('Error fetching date details:', error);
      toast.error('Failed to load order details');
    } finally {
      setLoadingDateDetail(false);
    }
  };

  const handleCardClick = (type) => {
    if (!analytics) return;
    
    setCardDetailType(type);
    
    // Prepare data based on card type
    let detailData = {};
    
    switch(type) {
      case 'sales':
        detailData = {
          title: 'Total Sales Details',
          description: `Sales breakdown for ${dateRange.start} to ${dateRange.end}`,
          data: analytics.sales_by_date,
          type: 'sales'
        };
        break;
      case 'items_sold':
        detailData = {
          title: 'Items Sold Details',
          description: 'Top selling items in the selected period',
          data: analytics.top_selling_items,
          type: 'items_sold'
        };
        break;
      case 'items_added':
        detailData = {
          title: 'Items Added to Inventory',
          description: 'Inventory additions by date',
          data: analytics.items_added_by_date,
          type: 'items_added'
        };
        break;
      case 'returns':
        detailData = {
          title: 'Returned Items',
          description: 'Returns breakdown by date',
          data: analytics.items_returned_by_date,
          type: 'returns'
        };
        break;
      case 'net_change':
        detailData = {
          title: 'Net Inventory Change',
          description: 'Detailed breakdown of inventory changes',
          data: {
            items_added: analytics.summary.total_items_added,
            items_sold: analytics.summary.total_items_sold,
            returns: analytics.summary.total_returns,
            net_change: analytics.summary.net_inventory_change
          },
          type: 'net_change'
        };
        break;
      default:
        return;
    }
    
    setCardDetailData(detailData);
    setShowCardDetailDialog(true);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file && file.name.endsWith('.csv')) {
      setSelectedFile(file);
    } else {
      toast.error('Please select a valid CSV file');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await axios.post(
        `${API}/inventory/import-to-shopify?store_name=${selectedStore}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 300000 // 5 minutes timeout
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedLogDetail(response.data);
        setShowLogsDialog(true);
        fetchImportLogs();
        setSelectedFile(null);
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      success: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      throttled: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      success_after_retry: 'bg-blue-100 text-blue-800 border-blue-200',
      error: 'bg-red-100 text-red-800 border-red-200'
    };

    return (
      <Badge variant="outline" className={variants[status] || 'bg-gray-100'}>
        {status}
      </Badge>
    );
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Inventory Import & Analytics</h1>
          <p className="text-gray-600 mt-1">Import products to Shopify and track inventory movements</p>
        </div>
      </div>

      {/* CSV Import Section */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Import Products to Shopify
          </CardTitle>
          <CardDescription>
            Upload a CSV file in Shopify format to bulk import products
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="store-select">Select Store</Label>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger id="store-select">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ashmiaa">Ashmiaa (ashmiaa.myshopify.com)</SelectItem>
                  {stores.map((store) => (
                    <SelectItem key={store.store_name} value={store.store_name}>
                      {store.store_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                disabled={importing}
              />
              {selectedFile && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ {selectedFile.name}
                </p>
              )}
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleImport}
                disabled={!selectedFile || importing}
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                {importing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import to Shopify
                  </>
                )}
              </Button>
            </div>
          </div>

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 font-semibold mb-2">CSV Format Requirements:</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Handle, Title, Body (HTML), Vendor, Type, Tags</li>
              <li>• Option1/2/3 Name & Value, Variant SKU, Variant Price</li>
              <li>• Variant Inventory Qty, Variant Compare At Price, Image Src</li>
              <li>• Uses Shopify's standard product export format</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Date Analytics Section */}
      <Card className="border-gray-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Inventory Date Analytics
              </CardTitle>
              <CardDescription>
                Track inventory movements, sales, and returns by date (fulfilled orders only)
                <br />
                <span className="text-orange-600 text-xs">⚠️ Note: Counts all SKUs in fulfilled orders. Partial fulfillments not distinguished.</span>
              </CardDescription>
            </div>
            <Badge variant="outline" className="bg-green-100 text-green-700 border-green-300">
              <CheckCircle className="w-3 h-3 mr-1" />
              Fulfilled Orders Only
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label htmlFor="analytics-store">Select Store</Label>
              <Select value={selectedAnalyticsStore} onValueChange={setSelectedAnalyticsStore}>
                <SelectTrigger id="analytics-store">
                  <SelectValue placeholder="Select store" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  <SelectItem value="ashmiaa">ashmiaa</SelectItem>
                  <SelectItem value="tnvcollectionpk">tnvcollectionpk</SelectItem>
                  <SelectItem value="tnvcollection">tnvcollection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="start-date">Start Date</Label>
              <Input
                id="start-date"
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="end-date">End Date</Label>
              <Input
                id="end-date"
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
            <div className="flex items-end">
              <Button
                onClick={fetchDateAnalytics}
                disabled={loadingAnalytics}
                className="w-full bg-purple-600 hover:bg-purple-700"
              >
                {loadingAnalytics ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    Fetch Analytics
                  </>
                )}
              </Button>
            </div>
          </div>

          {analytics && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <p className="text-sm text-gray-600 mb-2">💡 Click any card for detailed breakdown</p>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card 
                  className="border-green-200 bg-green-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleCardClick('sales')}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-green-600 font-medium">Total Sales</p>
                        <p className="text-2xl font-bold text-green-700">
                          Rs. {analytics.summary.total_sales.toLocaleString()}
                        </p>
                      </div>
                      <DollarSign className="w-8 h-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="border-blue-200 bg-blue-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleCardClick('items_sold')}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-blue-600 font-medium">Items Sold</p>
                        <p className="text-2xl font-bold text-blue-700">
                          {analytics.summary.total_items_sold}
                        </p>
                      </div>
                      <TrendingDown className="w-8 h-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="border-purple-200 bg-purple-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleCardClick('items_added')}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-purple-600 font-medium">Items Added</p>
                        <p className="text-2xl font-bold text-purple-700">
                          {analytics.summary.total_items_added}
                        </p>
                      </div>
                      <TrendingUp className="w-8 h-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="border-orange-200 bg-orange-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleCardClick('returns')}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-orange-600 font-medium">Returns</p>
                        <p className="text-2xl font-bold text-orange-700">
                          {analytics.summary.total_returns}
                        </p>
                      </div>
                      <Package className="w-8 h-8 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card 
                  className="border-gray-200 bg-gray-50 cursor-pointer hover:shadow-lg transition-shadow"
                  onClick={() => handleCardClick('net_change')}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-600 font-medium">Net Change</p>
                        <p className="text-2xl font-bold text-gray-700">
                          {analytics.summary.net_inventory_change > 0 ? '+' : ''}
                          {analytics.summary.net_inventory_change}
                        </p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-gray-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sales by Date Table */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Sales by Date 
                  <span className="text-sm text-gray-500 ml-2">(Click for details)</span>
                </h3>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Date</TableHead>
                      <TableHead>Orders</TableHead>
                      <TableHead>Items Sold</TableHead>
                      <TableHead>Total Sales</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {analytics.sales_by_date.slice(0, 10).map((day) => (
                      <TableRow 
                        key={day._id}
                        onClick={() => fetchDateDetails(day._id)}
                        className="cursor-pointer hover:bg-blue-50 transition-colors"
                      >
                        <TableCell className="font-medium text-blue-600">{day._id}</TableCell>
                        <TableCell>{day.total_orders}</TableCell>
                        <TableCell>{day.total_items}</TableCell>
                        <TableCell className="text-green-600 font-semibold">
                          Rs. {day.total_sales.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Top Selling Items */}
              {analytics.top_selling_items.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Top Selling Items</h3>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>SKU</TableHead>
                        <TableHead>Quantity Sold</TableHead>
                        <TableHead>Orders</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.top_selling_items.slice(0, 10).map((item) => (
                        <TableRow key={item._id}>
                          <TableCell className="font-mono text-sm">{item._id}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700">
                              {item.quantity_sold}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.orders_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Logs Section */}
      <Card className="border-gray-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Recent Import Logs
          </CardTitle>
          <CardDescription>History of Shopify product imports</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Date</TableHead>
                <TableHead>Filename</TableHead>
                <TableHead>Store</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Success</TableHead>
                <TableHead>Failed</TableHead>
                <TableHead>Throttled</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {importLogs.map((log, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-sm">
                    {new Date(log.imported_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{log.filename}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{log.store_name}</Badge>
                  </TableCell>
                  <TableCell>{log.total_products}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-green-100 text-green-700">
                      {log.success_count}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-red-100 text-red-700">
                      {log.fail_count}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
                      {log.throttle_count}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedLogDetail(log);
                        setShowLogsDialog(true);
                      }}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Import Log Details Dialog */}
      <Dialog open={showLogsDialog} onOpenChange={setShowLogsDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Details</DialogTitle>
            <DialogDescription>
              {selectedLogDetail?.filename} - {selectedLogDetail?.store_name}
            </DialogDescription>
          </DialogHeader>

          {selectedLogDetail && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-gray-200">
                  <CardContent className="pt-4">
                    <p className="text-xs text-gray-600">Total Products</p>
                    <p className="text-2xl font-bold">{selectedLogDetail.total_products}</p>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <p className="text-xs text-green-600">Success</p>
                    <p className="text-2xl font-bold text-green-700">
                      {selectedLogDetail.success_count}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="pt-4">
                    <p className="text-xs text-red-600">Failed</p>
                    <p className="text-2xl font-bold text-red-700">
                      {selectedLogDetail.fail_count}
                    </p>
                  </CardContent>
                </Card>
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="pt-4">
                    <p className="text-xs text-yellow-600">Throttled</p>
                    <p className="text-2xl font-bold text-yellow-700">
                      {selectedLogDetail.throttle_count}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Import Log</h4>
                <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Product</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Message</TableHead>
                        <TableHead>Variants</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedLogDetail.import_log?.map((log, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{log.title}</p>
                              <p className="text-xs text-gray-500">{log.handle}</p>
                            </div>
                          </TableCell>
                          <TableCell>{getStatusBadge(log.status)}</TableCell>
                          <TableCell className="text-sm">
                            {log.message || log.error || '—'}
                          </TableCell>
                          <TableCell>{log.variants_count || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowLogsDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Detail Dialog */}
      <Dialog open={showDateDetailDialog} onOpenChange={setShowDateDetailDialog}>
        <DialogContent className="max-w-6xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Orders for {dateDetailData?.date}</DialogTitle>
            <DialogDescription>
              Detailed view of all fulfilled orders for this date
            </DialogDescription>
          </DialogHeader>

          {loadingDateDetail ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-600">Loading order details...</span>
            </div>
          ) : dateDetailData && (
            <div className="space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="border-blue-200 bg-blue-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-700">
                        {dateDetailData.summary.total_orders}
                      </p>
                      <p className="text-xs text-blue-600">Total Orders</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-purple-200 bg-purple-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-700">
                        {dateDetailData.summary.total_skus}
                      </p>
                      <p className="text-xs text-purple-600">Total SKUs</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-green-700">
                        Rs. {dateDetailData.summary.total_value.toLocaleString()}
                      </p>
                      <p className="text-xs text-green-600">Total Value</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-gray-200 bg-gray-50">
                  <CardContent className="pt-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-700">
                        {dateDetailData.summary.fulfilled}/{dateDetailData.summary.unfulfilled}
                      </p>
                      <p className="text-xs text-gray-600">Fulfilled/Unfulfilled</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Orders Table */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Order Details</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Order #</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Fulfillment</TableHead>
                        <TableHead>Delivery Status</TableHead>
                        <TableHead>SKUs</TableHead>
                        <TableHead>Amount</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {dateDetailData.orders.map((order, idx) => (
                        <TableRow key={idx} className="hover:bg-gray-50">
                          <TableCell className="font-mono text-sm font-medium">
                            #{order.order_number}
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="text-sm font-medium">
                                {order.first_name} {order.last_name}
                              </p>
                              {order.tracking_number && (
                                <p className="text-xs text-gray-500">
                                  {order.tracking_number}
                                </p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant="outline"
                              className={
                                order.fulfillment_status === 'fulfilled'
                                  ? 'bg-green-100 text-green-700 border-green-300'
                                  : 'bg-yellow-100 text-yellow-700 border-yellow-300'
                              }
                            >
                              {order.fulfillment_status || 'unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
                              {order.delivery_status || 'N/A'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {order.order_skus?.slice(0, 3).map((sku, i) => (
                                <Badge key={i} variant="outline" className="text-xs bg-gray-50">
                                  {sku}
                                </Badge>
                              ))}
                              {order.order_skus?.length > 3 && (
                                <Badge variant="outline" className="text-xs bg-gray-100">
                                  +{order.order_skus.length - 3} more
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold text-green-600">
                            Rs. {order.total_spent?.toLocaleString()}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowDateDetailDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Card Detail Dialog */}
      <Dialog open={showCardDetailDialog} onOpenChange={setShowCardDetailDialog}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{cardDetailData?.title}</DialogTitle>
            <DialogDescription>{cardDetailData?.description}</DialogDescription>
          </DialogHeader>

          {cardDetailData && (
            <div className="space-y-4">
              {/* Sales Breakdown */}
              {cardDetailData.type === 'sales' && (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Date</TableHead>
                        <TableHead>Orders</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Total Sales</TableHead>
                        <TableHead>Avg Order Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cardDetailData.data.map((day) => (
                        <TableRow key={day._id} className="hover:bg-gray-50">
                          <TableCell className="font-medium">{day._id}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-50">
                              {day.total_orders}
                            </Badge>
                          </TableCell>
                          <TableCell>{day.total_items}</TableCell>
                          <TableCell className="font-semibold text-green-600">
                            Rs. {day.total_sales.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-gray-600">
                            Rs. {(day.total_sales / day.total_orders).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Items Sold Breakdown */}
              {cardDetailData.type === 'items_sold' && (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Rank</TableHead>
                        <TableHead>SKU</TableHead>
                        <TableHead>Quantity Sold</TableHead>
                        <TableHead>Orders</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cardDetailData.data.map((item, idx) => (
                        <TableRow key={item._id} className="hover:bg-gray-50">
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={
                                idx === 0 
                                  ? 'bg-yellow-100 text-yellow-800 border-yellow-300' 
                                  : idx === 1
                                  ? 'bg-gray-200 text-gray-800 border-gray-300'
                                  : idx === 2
                                  ? 'bg-orange-100 text-orange-800 border-orange-300'
                                  : 'bg-gray-50'
                              }
                            >
                              #{idx + 1}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {item._id}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="bg-blue-100 text-blue-700">
                              {item.quantity_sold}
                            </Badge>
                          </TableCell>
                          <TableCell>{item.orders_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Items Added Breakdown */}
              {cardDetailData.type === 'items_added' && (
                <div>
                  {cardDetailData.data.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Date</TableHead>
                          <TableHead>Items Added</TableHead>
                          <TableHead>Total Cost</TableHead>
                          <TableHead>Avg Cost per Item</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cardDetailData.data.map((day) => (
                          <TableRow key={day._id} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{day._id}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-purple-50 text-purple-700">
                                {day.items_added}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-green-600">
                              Rs. {day.total_cost.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              Rs. {(day.total_cost / day.items_added).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No items added in the selected date range
                    </div>
                  )}
                </div>
              )}

              {/* Returns Breakdown */}
              {cardDetailData.type === 'returns' && (
                <div>
                  {cardDetailData.data.length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Date</TableHead>
                          <TableHead>Items Returned</TableHead>
                          <TableHead>Return Value</TableHead>
                          <TableHead>Avg Return Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {cardDetailData.data.map((day) => (
                          <TableRow key={day._id} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{day._id}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="bg-orange-50 text-orange-700">
                                {day.items_returned}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-semibold text-red-600">
                              Rs. {day.return_value.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-gray-600">
                              Rs. {(day.return_value / day.items_returned).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      No returns in the selected date range
                    </div>
                  )}
                </div>
              )}

              {/* Net Change Breakdown */}
              {cardDetailData.type === 'net_change' && (
                <div className="space-y-4">
                  <Card className="border-blue-100 bg-blue-50">
                    <CardContent className="pt-4">
                      <h4 className="text-lg font-semibold text-blue-900 mb-4">
                        Inventory Movement Summary
                      </h4>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-purple-600" />
                            <span className="font-medium text-gray-700">Items Added</span>
                          </div>
                          <span className="text-2xl font-bold text-purple-700">
                            +{cardDetailData.data.items_added}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <TrendingDown className="w-5 h-5 text-blue-600" />
                            <span className="font-medium text-gray-700">Items Sold</span>
                          </div>
                          <span className="text-2xl font-bold text-blue-700">
                            -{cardDetailData.data.items_sold}
                          </span>
                        </div>
                        <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div className="flex items-center gap-2">
                            <Package className="w-5 h-5 text-orange-600" />
                            <span className="font-medium text-gray-700">Returns Received</span>
                          </div>
                          <span className="text-2xl font-bold text-orange-700">
                            +{cardDetailData.data.returns}
                          </span>
                        </div>
                        <div className="border-t-2 border-gray-300 pt-3 mt-3">
                          <div className="flex justify-between items-center p-3 bg-gradient-to-r from-gray-100 to-gray-200 rounded-lg">
                            <div className="flex items-center gap-2">
                              <BarChart3 className="w-5 h-5 text-gray-700" />
                              <span className="font-bold text-gray-900">Net Change</span>
                            </div>
                            <span className={`text-3xl font-bold ${
                              cardDetailData.data.net_change > 0 
                                ? 'text-green-700' 
                                : cardDetailData.data.net_change < 0
                                ? 'text-red-700'
                                : 'text-gray-700'
                            }`}>
                              {cardDetailData.data.net_change > 0 ? '+' : ''}
                              {cardDetailData.data.net_change}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="text-sm text-gray-600 bg-gray-50 p-4 rounded-lg">
                    <p className="font-semibold mb-2">Calculation:</p>
                    <p>Net Change = Items Added - Items Sold + Returns Received</p>
                    <p className="mt-2">
                      = {cardDetailData.data.items_added} - {cardDetailData.data.items_sold} + {cardDetailData.data.returns}
                      = {cardDetailData.data.net_change}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowCardDetailDialog(false)} variant="outline">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InventoryImport;
